import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { formatTwd } from "@/lib/utils/aiCost";
import { describeAiError } from "@/lib/utils/aiCredits";
import type { SlideTemplate } from "@ai-teach/types";
import { LoadingSpinner } from "@ai-teach/ui-web";
import { useState } from "react";
import {
  HiChevronDown,
  HiChevronRight,
  HiOutlineExclamationCircle,
  HiOutlineLightBulb,
  HiOutlineRefresh,
  HiOutlineTrash,
} from "react-icons/hi";
import { DraftFieldEditor } from "./DraftFieldEditor";
import type { PickedPhonics } from "./PhonicsLibraryPicker";
import type { DraftCheck, DraftCheckKind, DraftSlide, EditableField } from "./draftFields";

const CHECK_LABEL: Record<DraftCheckKind, string> = {
  wrongAnswer: "Answer",
  aiSounding: "Wording",
  offBook: "Off book",
};

const ELEMENT_KEY = /^element:(\d+):/;

interface SoundGroup {
  sound: EditableField;
  /** The script spoken when the sound is left empty; absent on elements that have none. */
  script: EditableField | undefined;
}

const elementOf = (field: EditableField) => ELEMENT_KEY.exec(field.key)?.[1] ?? "";

/** Says which of the two the learner will actually hear, since only one of them is used. */
function soundHint(sound: EditableField, script: EditableField | undefined): string {
  const isPicked = sound.value.kind === "audio" && sound.value.src !== null;
  if (!script) return "This sound plays as it is.";
  return isPicked
    ? "This sound plays; the script is not spoken."
    : "The script is spoken unless you pick a sound.";
}

/**
 * A sound is shown with the script of its own element, as one control: on a slide with several
 * audio elements a script standing on its own says nothing about which sound it feeds.
 */
export function splitFields(fields: EditableField[]) {
  const sounds: SoundGroup[] = fields
    .filter((field) => field.value.kind === "audio")
    .map((sound) => ({
      sound,
      script: fields.find((one) => one.key === `element:${elementOf(sound)}:script`),
    }));

  const spoken = new Set(sounds.flatMap(({ script }) => (script ? [script.key] : [])));

  return {
    sounds,
    pictures: fields.filter((field) => field.value.kind === "image"),
    wording: fields.filter(
      (field) =>
        field.value.kind !== "image" && field.value.kind !== "audio" && !spoken.has(field.key)
    ),
  };
}

interface SectionGroup {
  sectionId: string;
  template: string;
  slides: { slide: DraftSlide; index: number }[];
}

function groupBySection(slides: DraftSlide[]): SectionGroup[] {
  const groups = new Map<string, SectionGroup>();
  slides.forEach((slide, index) => {
    const group = groups.get(slide.sectionId);
    if (group) group.slides.push({ slide, index });
    else
      groups.set(slide.sectionId, {
        sectionId: slide.sectionId,
        template: slide.template,
        slides: [{ slide, index }],
      });
  });
  return [...groups.values()];
}

/** Pictures sit in their own column, so a sheet of slides can be filled without much scrolling. */
function SlideCard({
  slide,
  position,
  checks,
  onFieldsChange,
  onDelete,
  onRestore,
}: {
  slide: DraftSlide;
  position: number;
  checks: DraftCheck[];
  onFieldsChange: (fields: EditableField[]) => void;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const { wording, sounds, pictures } = splitFields(slide.fields);

  // A phonics clip is browsed by sound, and the sound it records names the spelling shown with it.
  const pickPhonics =
    slide.template === "spellingToSound"
      ? (sound: EditableField) => (picked: PickedPhonics) => {
          const spelling = slide.fields.find((one) => one.key === "slideData:phoneme");
          onFieldsChange([
            {
              ...sound,
              value: { kind: "audio", ...picked.media, phonemeId: picked.phonemeId },
            },
            ...(spelling
              ? [{ ...spelling, value: { kind: "text" as const, text: picked.phoneme } }]
              : []),
          ]);
        }
      : null;

  if (slide.deleted) {
    return (
      <li className="flex items-center gap-3 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
        <span className="w-4 shrink-0 font-mono text-[11px] text-gray-400">{position}</span>
        <span className="min-w-0 flex-1 truncate line-through">{slide.name}</span>
        <button
          type="button"
          onClick={onRestore}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-primary-600 hover:bg-primary-500/10 dark:text-primary-300"
        >
          Keep it after all
        </button>
      </li>
    );
  }

  return (
    <li
      className={`flex gap-3 rounded-lg border px-3 py-2 ${
        slide.error
          ? "border-warning-500/60 bg-warning-500/5"
          : checks.length > 0
            ? "border-accent-500/50"
            : "border-gray-200 dark:border-gray-800"
      }`}
    >
      <span className="w-4 shrink-0 pt-1.5 font-mono text-[11px] text-gray-400">{position}</span>
      <div className="min-w-0 flex-1 space-y-1.5">
        {slide.parentSlideName && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            under {slide.parentSlideName}
          </p>
        )}
        {slide.error && (
          <p className="flex items-start gap-1.5 rounded-md bg-warning-500/10 px-2 py-1.5 text-[11px] leading-relaxed text-warning-700 dark:text-warning-500">
            <HiOutlineExclamationCircle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              {describeAiError(slide.error).replace(/\.$/, "")}. The example's own wording is below
              — write over it, or drop this slide.
            </span>
          </p>
        )}
        {checks.map((check) => (
          <p
            key={`${check.kind}-${check.fieldKey}-${check.note}`}
            className="flex items-start gap-1.5 rounded-md bg-accent-500/10 px-2 py-1.5 text-[11px] leading-relaxed text-accent-700 dark:text-accent-400"
          >
            <HiOutlineLightBulb className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-semibold">{CHECK_LABEL[check.kind]}:</span> {check.note}
            </span>
          </p>
        ))}
        {wording.map((field) => (
          <DraftFieldEditor
            key={field.key}
            field={field}
            onChange={(one) => onFieldsChange([one])}
          />
        ))}

        {sounds.map(({ sound, script }) => (
          <div
            key={sound.key}
            className="flex items-start gap-3 rounded-lg border border-gray-200 p-2 dark:border-gray-800"
          >
            <div className="min-w-0 flex-1 space-y-1">
              {script && (
                <DraftFieldEditor field={script} onChange={(one) => onFieldsChange([one])} />
              )}
              <p className="text-[10px] text-gray-400">{soundHint(sound, script)}</p>
            </div>
            <DraftFieldEditor
              field={sound}
              onChange={(one) => onFieldsChange([one])}
              onPickPhonics={pickPhonics?.(sound)}
            />
          </div>
        ))}

        {pictures.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-0.5">
            {pictures.map((field) => (
              <DraftFieldEditor
                key={field.key}
                field={field}
                onChange={(one) => onFieldsChange([one])}
              />
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Drop ${slide.name}`}
        className="h-7 shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-error-500/10 hover:text-error-500"
      >
        <HiOutlineTrash className="h-4 w-4" />
      </button>
    </li>
  );
}

function SectionBlock({
  group,
  name,
  checks,
  costUsd,
  isRedrafting,
  isBusy,
  onFieldsChange,
  onDeletedChange,
  onSectionDeletedChange,
  onRedraft,
}: {
  group: SectionGroup;
  name: string | undefined;
  checks: Map<string, DraftCheck[]>;
  /** What the model cost on this section, redrafts included. Undefined before the ledger answers. */
  costUsd: number | undefined;
  isRedrafting: boolean;
  isBusy: boolean;
  onFieldsChange: (slideIndex: number, fields: EditableField[]) => void;
  onDeletedChange: (slideIndex: number, deleted: boolean) => void;
  onSectionDeletedChange: (sectionId: string, deleted: boolean) => void;
  onRedraft: (sectionId: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const count = group.slides.filter(({ slide }) => !slide.deleted).length;
  const problems = group.slides.filter(({ slide }) => slide.error && !slide.deleted).length;
  const flagged = group.slides.filter(
    ({ slide }) => !slide.deleted && slide.id && checks.has(slide.id)
  ).length;
  const isDropped = count === 0;

  return (
    <section className="space-y-1.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {isOpen ? (
            <HiChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
          ) : (
            <HiChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
          )}
          <span className={`card-title truncate ${isDropped ? "text-gray-400 line-through" : ""}`}>
            {name ?? getTemplateLabel(group.template as SlideTemplate)}
          </span>
          <span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
            {count} slide{count === 1 ? "" : "s"}
          </span>
          {problems > 0 && (
            <span className="shrink-0 rounded-full bg-warning-500/10 px-2 py-0.5 text-[11px] font-semibold text-warning-700 dark:text-warning-500">
              {problems} need{problems === 1 ? "s" : ""} a look
            </span>
          )}
          {flagged > 0 && (
            <span className="shrink-0 rounded-full bg-accent-500/10 px-2 py-0.5 text-[11px] font-semibold text-accent-600 dark:text-accent-400">
              {flagged} flagged
            </span>
          )}
          {costUsd !== undefined && (
            <span className="shrink-0 font-mono text-[11px] text-gray-400">
              {formatTwd(costUsd)}
            </span>
          )}
        </button>

        {isRedrafting ? (
          <span className="flex shrink-0 items-center gap-1.5 pr-1.5 text-xs text-gray-500 dark:text-gray-400">
            <LoadingSpinner className="h-3.5 w-3.5" />
            Redrafting…
          </span>
        ) : isDropped ? (
          <button
            type="button"
            onClick={() => onSectionDeletedChange(group.sectionId, false)}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-500/10 dark:text-primary-300"
          >
            Keep them after all
          </button>
        ) : (
          <div className="flex shrink-0 gap-0.5">
            <button
              type="button"
              onClick={() => onRedraft(group.sectionId)}
              disabled={isBusy}
              aria-label="Redraft section"
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-700 disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-white/5 dark:hover:text-gray-200"
            >
              <HiOutlineRefresh className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onSectionDeletedChange(group.sectionId, true)}
              disabled={isBusy}
              aria-label="Drop section"
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-error-500/10 hover:text-error-500 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <HiOutlineTrash className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {isOpen && (
        <ul className={`space-y-1.5 pl-6 ${isRedrafting ? "opacity-50" : ""}`}>
          {group.slides.map(({ slide, index }, position) => (
            <SlideCard
              key={slide.id ?? `${slide.sectionId}-${index}`}
              slide={slide}
              position={position + 1}
              checks={(slide.id ? checks.get(slide.id) : undefined) ?? []}
              onFieldsChange={(fields) => onFieldsChange(index, fields)}
              onDelete={() => onDeletedChange(index, true)}
              onRestore={() => onDeletedChange(index, false)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function DraftSheet({
  slides,
  sectionNames,
  checks,
  sectionCosts,
  redraftingSectionId,
  onFieldsChange,
  onDeletedChange,
  onSectionDeletedChange,
  onRedraft,
}: {
  slides: DraftSlide[];
  sectionNames: Map<string, string>;
  checks: Map<string, DraftCheck[]>;
  sectionCosts: Map<string, number>;
  redraftingSectionId: string | null;
  onFieldsChange: (slideIndex: number, fields: EditableField[]) => void;
  onDeletedChange: (slideIndex: number, deleted: boolean) => void;
  onSectionDeletedChange: (sectionId: string, deleted: boolean) => void;
  onRedraft: (sectionId: string) => void;
}) {
  return (
    <div className="space-y-4">
      {groupBySection(slides).map((group) => (
        <SectionBlock
          key={group.sectionId}
          group={group}
          name={sectionNames.get(group.sectionId)}
          checks={checks}
          costUsd={sectionCosts.get(group.sectionId)}
          isRedrafting={redraftingSectionId === group.sectionId}
          isBusy={redraftingSectionId !== null}
          onFieldsChange={onFieldsChange}
          onDeletedChange={onDeletedChange}
          onSectionDeletedChange={onSectionDeletedChange}
          onRedraft={onRedraft}
        />
      ))}
    </div>
  );
}
